import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Link,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useRecoilState } from "recoil";
import { refreshGlobal } from "../../../../atoms/refreshGlobal";
import TitleFlex from "../../vendasDigitadas/defaultComponents/titleFlex";
import {
  CNPJFormater,
  CPFFormater,
} from "../../../../defaultFunctions/formaters";
import { dateForList } from "../../../../constants/dataFormat";
import copy from "copy-to-clipboard";
import { customColors } from "../../../../atoms/customColors";
import { toast } from "react-toastify";
import { VendaBoleto } from "../PaymetLote";
import LoteStatusProgresso from "./LoteStatusProgresso";

interface ModalRevisãoVendaBoletoLoteProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  batchObjects: VendaBoleto[];
  setBatchObjects: (objects: VendaBoleto[]) => void;
  setBatchResponses: (responses: any[]) => void;
  setBoleto: (boleto: VendaBoleto) => void;
  initialValues: VendaBoleto;
  setLoadingState: (loading: boolean) => void;
  numBoletos: number;
  cliente: number;
  idLogin: number;
  handleBatchCreation: () => Promise<any>;
}

const getInterestDisplayText = (regrasJuros: any) => {
  if (!regrasJuros || !regrasJuros.mode || regrasJuros.mode === "SEM_JUROS") {
    return "Sem Juros";
  }

  const value = parseFloat(regrasJuros.value) / 100;

  if (regrasJuros.mode === "FIXED") {
    if (regrasJuros.frequency === "DAILY") {
      return `Valor Diário: R$ ${value.toFixed(2)}`;
    }
  } else if (regrasJuros.mode === "PERCENTAGE") {
    if (regrasJuros.frequency === "DAILY") {
      return `Percentual Diário: ${value.toFixed(3)}%`;
    } else if (regrasJuros.frequency === "MONTHLY") {
      return `Percentual Mensal: ${value.toFixed(2)}%`;
    } else if (regrasJuros.frequency === "ANNUALLY") {
      return `Percentual Anual: ${value.toFixed(2)}%`;
    }
  }

  return "Sem Juros";
};

const getMultaDisplayText = (regrasMulta: any) => {
  if (!regrasMulta || !regrasMulta.mode || regrasMulta.mode === "SEM_MULTA") {
    return "Sem Multa";
  }

  const value = parseFloat(regrasMulta.value) / 100;

  if (regrasMulta.mode === "FIXED") {
    return `Valor Fixo: R$ ${value.toFixed(2)}`;
  } else if (regrasMulta.mode === "PERCENTAGE") {
    return `Percentual: ${value.toFixed(2)}%`;
  }

  return "Sem Multa";
};

const getDescontoDisplayText = (regrasDesconto: any) => {
  if (
    !regrasDesconto ||
    !regrasDesconto.mode ||
    regrasDesconto.mode === "SEM_DESCONTO"
  ) {
    return "Sem Desconto";
  }

  const value = parseFloat(regrasDesconto.value) / 100;
  const dias = Number(regrasDesconto.diasAntesVencimento) || 0;
  const quando =
    dias > 0
      ? ` (até ${dias} dia(s) antes do vencimento)`
      : " (até o vencimento)";

  if (regrasDesconto.mode === "FIXED") {
    return `Valor Fixo: R$ ${value.toFixed(2)}${quando}`;
  } else if (regrasDesconto.mode === "PERCENTAGE") {
    return `Percentual: ${value.toFixed(2)}%${quando}`;
  }

  return "Sem Desconto";
};

export default function ModalRevisãoVendaBoletoLote({
  isOpen,
  setIsOpen,
  batchObjects,
  setBatchObjects,
  setBatchResponses,
  setBoleto,
  initialValues,
  setLoadingState,
  numBoletos,
  cliente,
  idLogin,
  handleBatchCreation,
}: ModalRevisãoVendaBoletoLoteProps) {
  const finalRef = React.useRef(null);
  const navigate = useNavigate();
  const [refresh, setRefreshGlobal] = useRecoilState(refreshGlobal);
  const colors = useRecoilState(customColors)[0];
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [loteId, setLoteId] = useState<string | null>(null);
  const [localBatchResponses, setLocalBatchResponses] = useState<any[]>([]);
  const [loadingState, setLocalLoadingState] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const onClose = () => {
    setIsOpen(false);
    setIsConfirmed(false);
    setLocalBatchResponses([]);
    setBatchObjects([]);
    setErrorMessage(null);
    setLoteId(null);
    setLoadingState(false);
  };

  const handleConfirm = async () => {
    setLocalLoadingState(true);
    setLoadingState(true);
    try {
      const result = await handleBatchCreation();
      // handleBatchCreation captura o erro internamente e retorna undefined.
      if (!result) {
        setErrorMessage("Erro ao criar o lote de boletos. Tente novamente.");
        return;
      }
      const boletos = result.boletos || [];
      setLocalBatchResponses(boletos);
      setBatchResponses(boletos);
      setLoteId(result.idLote || null);
      setIsConfirmed(true);
      setRefreshGlobal(0);
      toast.success(`${numBoletos} boleto(s) criado(s) com sucesso!`);
    } catch (error) {
      setErrorMessage("Erro ao criar o lote de boletos. Tente novamente.");
      toast.error("Erro ao criar o lote de boletos.");
    } finally {
      setLocalLoadingState(false);
      setLoadingState(false);
    }
  };

  const totalValue = batchObjects.reduce((sum, obj, index) => {
    return sum + (index === 0 ? obj.valorPrimeiraEmissao : obj.valor);
  }, 0);

  const downloadCSV = () => {
    const headers = ["Parcela", "Código de Barras", "ID da Transação", "URL"];
    const rows = localBatchResponses.map((response, index) => [
      index + 1,
      response.barcode || "",
      response.idTransacao || "",
      response.url || "",
    ]);
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `lote_boletos_${moment().format("YYYYMMDD_HHmmss")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const successfulBoletos = localBatchResponses.filter(
    (res) => res.idTransacao
  ).length;
  const failedBoletos = localBatchResponses.length - successfulBoletos;

  return (
    <Modal
      finalFocusRef={finalRef}
      isOpen={isOpen}
      onClose={onClose}
      size={"4xl"}
      scrollBehavior="inside"
    >
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent>
        <ModalHeader>
          <Flex w={"100%"} justifyContent={"center"}>
            <Text fontWeight={"bold"} color={colors.GREENMONEDD}>
              {isConfirmed
                ? "Lote de Boletos Criado!"
                : "Revisar Lote de Boletos"}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          {batchObjects.length > 0 && (
            <>
              <TitleFlex item={"Resumo do Lote"} />
              <Flex flexDir={"column"} px={4}>
                <Text>
                  <strong>Número de Boletos:</strong> {numBoletos}
                </Text>
                <Text>
                  <strong>Valor Total:</strong>{" "}
                  {totalValue.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </Text>
                <Text>
                  <strong>Valor da Primeira Parcela:</strong>{" "}
                  {batchObjects[0].valorPrimeiraEmissao.toLocaleString(
                    "pt-BR",
                    { style: "currency", currency: "BRL" }
                  )}
                </Text>
                <Text>
                  <strong>Valor das Demais Parcelas:</strong>{" "}
                  {batchObjects[1]?.valor.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }) || "N/A"}
                </Text>
                <Text>
                  <strong>Descrição Base:</strong> {batchObjects[0].descricao}
                </Text>
                <Text>
                  <strong>Segunda Parcela no Mesmo Mês:</strong>{" "}
                  {batchObjects[0].mesesPular === -1 ? "Sim" : "Não"}
                </Text>
                {batchObjects[0].mesesPular !== -1 && (
                  <Text>
                    <strong>Meses a Pular:</strong> {batchObjects[0].mesesPular}
                  </Text>
                )}
              </Flex>

              <TitleFlex item={`Parcelas a Emitir (${batchObjects.length})`} />
              <Box
                maxH="320px"
                overflowY="auto"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                mx={4}
              >
                <Table variant="simple" size="sm">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                      <Th>Parcela</Th>
                      <Th>Vencimento</Th>
                      <Th>Limite Pgto</Th>
                      <Th isNumeric>Valor</Th>
                      <Th isNumeric>Desconto</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {batchObjects.map((obj, index) => {
                      const valorParcela =
                        index === 0 ? obj.valorPrimeiraEmissao : obj.valor;
                      const temDesc =
                        !!obj.RegrasDesconto &&
                        obj.RegrasDesconto.mode !== "SEM_DESCONTO";
                      const descVal = temDesc
                        ? parseFloat(String(obj.RegrasDesconto!.value)) / 100
                        : 0;
                      return (
                        <Tr key={index}>
                          <Td>
                            {index === 0 ? "1ª (entrada)" : `${index + 1}ª`}
                          </Td>
                          <Td>{moment(obj.vencimento).format(dateForList)}</Td>
                          <Td>
                            {obj.dataLimitePagamento
                              ? moment(obj.dataLimitePagamento).format(
                                dateForList
                              )
                              : "—"}
                          </Td>
                          <Td isNumeric>
                            {valorParcela.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </Td>
                          <Td isNumeric>
                            {temDesc
                              ? `- ${descVal.toLocaleString("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                              })}`
                              : "—"}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>

              <TitleFlex item={"Dados do Pagador"} />
              <Flex flexDir={"column"} px={4}>
                <Text>Nome do Pagador: {batchObjects[0].nomePagador}</Text>
                <Text>
                  Documento do Pagador:{" "}
                  {batchObjects[0].cpfPagador.length === 11
                    ? CPFFormater(batchObjects[0].cpfPagador)
                    : CNPJFormater(batchObjects[0].cpfPagador)}
                </Text>
                <Text>Email: {batchObjects[0].emailPagador}</Text>
                <Text>Número: {batchObjects[0].numeroPagador}</Text>
              </Flex>

              <TitleFlex item={"Endereço de Cobrança"} />
              <Flex flexDir={"column"} px={4}>
                <Text>
                  {`${batchObjects[0].enderRua}, ${batchObjects[0].enderNumero}/${batchObjects[0].enderBairro}-${batchObjects[0].enderCidade}-${batchObjects[0].enderEstado}`}
                </Text>
                <Text>CEP: {batchObjects[0].enderCEP}</Text>
                {batchObjects[0].enderComplemento && (
                  <Text>Complemento: {batchObjects[0].enderComplemento}</Text>
                )}
                {batchObjects[0].enderReferencia && (
                  <Text>Referência: {batchObjects[0].enderReferencia}</Text>
                )}
              </Flex>

              {batchObjects[0].permitirRegras && (
                <>
                  <TitleFlex item={"Regras de Juros, Multa e Desconto"} />
                  <Flex flexDir={"column"} px={4}>
                    <Text>
                      Juros:{" "}
                      {getInterestDisplayText(batchObjects[0].RegrasJuros)}
                    </Text>
                    <Text>
                      Multa: {getMultaDisplayText(batchObjects[0].RegrasMulta)}
                    </Text>
                    <Text>
                      Desconto:{" "}
                      {getDescontoDisplayText(batchObjects[0].RegrasDesconto)}
                    </Text>
                  </Flex>
                </>
              )}

              {isConfirmed && loteId && (
                <>
                  <TitleFlex item={"Geração das Parcelas"} />
                  <Box px={4}>
                    <LoteStatusProgresso idLote={loteId} />
                  </Box>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter justifyContent={"space-evenly"}>
          {!isConfirmed ? (
            <>
              <Button
                background={colors.GRAY}
                color={"white"}
                mr={3}
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                bgColor={colors.BLUEMONEDDBUTTONS}
                color={colors.WHITE}
                isDisabled={loadingState}
                onClick={handleConfirm}
              >
                {loadingState ? (
                  <Spinner size="sm" />
                ) : (
                  `Confirmar Criação de ${numBoletos} Boleto${
                    numBoletos > 1 ? "s" : ""
                  }`
                )}
              </Button>
            </>
          ) : (
            <Flex gap={3}>
              <Button
                background={colors.GRAY}
                color={"white"}
                onClick={onClose}
              >
                Fechar
              </Button>
              {localBatchResponses.length > 0 && (
                <Button
                  bgColor={colors.GREENMONEDD}
                  color={colors.WHITE}
                  onClick={downloadCSV}
                >
                  Baixar CSV
                </Button>
              )}
            </Flex>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
